import ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";

const EstimatingDashboardRoute: RouteType = {
  name: "Estimating Dashboard",
  path: "/estimating-dashboard",
  layout: "/app",
  group: "Estimating",
  requiresAuth: true,
  requiresPermission: false,
  viewportCard: {
    header: {
      title: "Estimating Dashboard",
      component: () => import("./EstimatingDashboardHeader"),
      enableRefreshButton: true,
      enableBackButton: false,
    },
    body: {
      component: () => import("./EstimatingDashboardBody"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore }) => {
    try {
      // Initialize the dashboard view store
      const EstimatingDashboardViewStore = (
        await import("./EstimatingDashboardViewStore")
      ).default;

      if (!rootStore.estimatingDashboardViewStore) {
        rootStore.estimatingDashboardViewStore =
          new EstimatingDashboardViewStore(rootStore);
      }

      // Load critical estimating stores
      const promises = [];

      // Load bids for quote pipeline metrics
      const bidsStore = rootStore.bidsStore as ServiceStore;
      if (bidsStore) {
        promises.push(bidsStore.find());
      }

      // Load manhole quotes
      const manholeQuotesStore = rootStore.manholeQuotesStore as ServiceStore;
      if (manholeQuotesStore) {
        promises.push(manholeQuotesStore.find());
      }

      // Load opportunities
      const opportunitiesStore = rootStore.opportunitiesStore as ServiceStore;
      if (opportunitiesStore) {
        promises.push(opportunitiesStore.find());
      }

      // Load users for estimator performance
      const userStore = rootStore.userStore as ServiceStore;
      if (userStore) {
        promises.push(userStore.find());
      }

      // Load bid analytics if available
      const bidAnalyticsStore = rootStore.bidAnalyticsStore as ServiceStore;
      if (bidAnalyticsStore) {
        promises.push(
          bidAnalyticsStore.find().catch(() => {
            console.warn("Bid analytics store not accessible");
          }),
        );
      }

      // Load opportunity scorer if available
      const opportunityScorerStore =
        rootStore.opportunityScorerStore as ServiceStore;
      if (opportunityScorerStore) {
        promises.push(
          opportunityScorerStore.find().catch(() => {
            console.warn("Opportunity scorer store not accessible");
          }),
        );
      }

      // Load projects for context
      const projectsStore = rootStore.projectsStore as ServiceStore;
      if (projectsStore) {
        promises.push(projectsStore.find());
      }

      // Load accounts for customer analysis
      const customersStore = rootStore.customersStore as ServiceStore;
      if (customersStore) {
        promises.push(customersStore.find());
      }

      // Load manhole projects for project pipeline
      const manholeProjectsStore =
        rootStore.manholeProjectsStore as ServiceStore;
      if (manholeProjectsStore) {
        promises.push(manholeProjectsStore.find());
      }

      // Load manhole structures for design metrics
      const manholeStructuresStore =
        rootStore.manholeStructuresStore as ServiceStore;
      if (manholeStructuresStore) {
        promises.push(manholeStructuresStore.find());
      }

      // Load manhole pipe connections for design metrics
      const manholePipeConnectionsStore =
        rootStore.manholePipeConnectionsStore as ServiceStore;
      if (manholePipeConnectionsStore) {
        promises.push(manholePipeConnectionsStore.find());
      }

      // Load manhole quote items for design metrics
      const manholeQuoteItemsStore =
        rootStore.manholeQuoteItemsStore as ServiceStore;
      if (manholeQuoteItemsStore) {
        promises.push(manholeQuoteItemsStore.find());
      }

      // Load sales orders for quote-to-order conversion tracking
      const salesOrdersStore = rootStore.salesOrdersStore as ServiceStore;
      if (salesOrdersStore) {
        promises.push(salesOrdersStore.find());
      }

      // Execute all data loading in parallel
      await Promise.allSettled(promises);

      // Load dashboard data after stores are populated
      const viewStore = (rootStore as any).estimatingDashboardViewStore;
      if (viewStore) {
        await viewStore.loadDashboardData();
      }

      return true;
    } catch (error) {
      console.error("Failed to load estimating dashboard data:", error);
      return true; // Allow navigation even if data loading fails
    }
  },
  onExit: ({ rootStore }: any) => {
    const viewStore = (rootStore as any).estimatingDashboardViewStore;
    if (viewStore) {
      viewStore.reset();
    }
  },
};

export default EstimatingDashboardRoute;
